import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import uuid from 'uuid';
import { Action, ButtonGroup } from '../../../../../../components';
import ModalConfirm from '../../../../../../components/modal-confirm/ModalConfirm';
import { Article } from '../../../../../../types/items';
import {
  addDownloadMedia,
  deleteArticleRequest,
  getArticleVideoMasterRequest,
  setDataExportMedia,
  setDownloadMedia,
  setOpenModalExportMedia,
} from '../../../../../../store/modules/articles/actions';
import styles from './styles';
import { ButtonOption } from '../../../../../../components/button/ButtonGroup';
import { selectCurrentDocumentPage } from '../../../../../../store/modules/articles/selectors';
import { selectIsSuperAdmin } from '../../../../../../store/modules/users/selectors';
import AdvancedActionCell from '../../../../../../components/action-cell/AdvancedActionCell';
import PageSelect from './PageSelect';

type Props = {
  media: Article;
  thumbnailHandler: () => void;
  downloadHandler: () => void;
  styleType?: 'desktop' | 'mobile';
};

const MediaDialogGroupActions = ({ media, thumbnailHandler, downloadHandler, styleType = 'desktop' }: Props) => {
  const classes = styles();
  const dispatch = useDispatch();
  const [confirmOpened, setConfirmOpened] = useState(false);
  const { can_edit: canEdit, can_delete: canDelete } = media.permissions;
  const isProcessingMedia = media.ref.document?.ref?.is_processing || false;
  const isSuperAdmin = useSelector(selectIsSuperAdmin);

  const deleteHandler = useCallback(() => {
    setConfirmOpened(true);
  }, [setConfirmOpened]);

  const closeConfirmHandler = useCallback(() => {
    setConfirmOpened(false);
  }, [setConfirmOpened]);

  const confirmHandler = useCallback(() => {
    closeConfirmHandler();
    dispatch(deleteArticleRequest(media.id));
  }, [closeConfirmHandler, dispatch, media.id]);

  const exportMediaHandler = useCallback(() => {
    dispatch(setDataExportMedia(media));
    dispatch(setOpenModalExportMedia(true));
  }, [dispatch, media]);

  const documentPage = useSelector(selectCurrentDocumentPage);

  const clickMasterRequestHandler = useCallback(() => {
    const { ref: { video } } = media;
    if (video) dispatch(getArticleVideoMasterRequest(video));
  }, [dispatch, media]);

  const downloadDocumentPageHandler = useCallback(() => {
    if (documentPage) {
      const downloadId = uuid();
      dispatch(
        addDownloadMedia({
          name: documentPage.filename,
          url: documentPage.url.url,
          downloadId,
          urlRequested: true,
        }),
      );
      dispatch(setDownloadMedia({
        name: documentPage.filename,
        url: documentPage.url.url,
        downloadId,
        urlRequested: false,
      }));
    }
  }, [dispatch, documentPage]);

  const isGeneratedStatics = media.ref.video?.generated_statics;

  let buttons: ButtonOption[] = [
    {
      title: (media.ref.document?.media_type_id === 4) ? 'Download Document' : 'Download',
      onClick: downloadHandler,
      disabled: isGeneratedStatics === false,
    },
    {
      title: 'Downloads & Exports',
      onClick: exportMediaHandler,
      disabled: false,
      isVideo: true,
    },
  ];

  if (!isProcessingMedia && media.ref.document?.media_type_id === 4) {
    buttons.push({
      title: 'Download Page',
      onClick: downloadDocumentPageHandler,
      disabled: false,
      isVideo: false,
    });
  }

  if (canEdit) {
    buttons.push({
      title: 'Set thumbnail',
      tooltip: 'Select the current video frame to be this video\'s thumbnail' as React.ReactNode,
      onClick: thumbnailHandler,
      disabled: false,
      isVideo: true,
    });
  }

  if (canDelete) {
    buttons.push({
      title: 'Delete',
      onClick: deleteHandler,
      disabled: false,
    });
  }

  if (isSuperAdmin) {
    buttons.push({
      title: 'Request Master',
      onClick: clickMasterRequestHandler,
      disabled: false,
      isVideo: true,
    });
  }

  if (media.ref.video === null) {
    buttons = buttons.filter((item) => !item.isVideo);
  }

  if (styleType === 'mobile') {
    return (
      <div className={classes.mobileMenu}>
        {media.ref.document?.media_type_id === 4 && <PageSelect media={media} className={classes.mobilePageSelector} />}
        <div>

          <AdvancedActionCell disabledPortal={false}>
            {buttons.map((btn, index) => (
              <Action
                key={index}
                title={btn.title || 'disabled'}
                onClick={btn.onClick}
                disabled={btn.disabled}
              />
            ))}
          </AdvancedActionCell>
        </div>

      </div>
    );
  }

  return (
    <>
      <ButtonGroup
        buttons={buttons}
        className={classes.groupAction}
        color="default"
        variant="text"
        overlay={false}
      />
      <ModalConfirm
        open={confirmOpened}
        onClose={closeConfirmHandler}
        onCancel={closeConfirmHandler}
        onConfirm={confirmHandler}
      />
    </>
  );
};

export default MediaDialogGroupActions;
